import { useIncludeCashFilterFunc } from '@src/hooks/useIncludeCashFilter';
import {
  removeEmptyFilters,
  useCallbackRef,
  useConstant,
  useWSFilterPipe,
  useWsBlotterTable,
  type AgGridMenuItems,
  type Column,
  type ContextMenuOptions,
  type Position,
  type UseWsBlotterTable,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, GridOptions, StateUpdatedEvent } from 'ag-grid-community';
import { useMemo } from 'react';
import type { SubAccountPositionsTabProps } from '../types';
import { getPositionAsset, getPositionKey } from './positionsSubAccountsRow';
import type { PositionsSubAccountsBlotterFoundation } from './usePositionsSubAccountsBlotterFoundation';
import { usePositionsSubAccountsBlotterGrouping } from './usePositionsSubAccountsBlotterGrouping';
import { usePositionsSubAccountsBlotterVariants } from './usePositionsSubAccountsBlotterVariants';
import { useSubAccountPositionsBlotterRequest } from './useSubAccountPositionsBlotterRequest';

export interface UsePositionsSubAccountsWsBlotterParams {
  blotterID: string;
  tab: SubAccountPositionsTabProps;
  foundation: PositionsSubAccountsBlotterFoundation;
  /** Grid column defs (desktop may append context-menu columns). */
  columns?: Column[];
  /** Merged after variant overlay; `onStateUpdated` is always owned by this hook. */
  gridOptions?: GridOptions<Position>;
  contextMenuOptions?: ContextMenuOptions<Position>;
  getExtraMainMenuItems?: (params: GetMainMenuItemsParams) => AgGridMenuItems;
  /** Allow grouping buttons to wrap onto multiple rows. */
  wrapGroupingToggleButtons?: boolean;
  /** Use shorter grouping button labels in constrained layouts. */
  useMobileLabels?: boolean;
}

export interface UsePositionsSubAccountsWsBlotterResult {
  blotterTable: UseWsBlotterTable<Position>;
  hierarchical: boolean;
  showZeroBalances: boolean;
  includeCash: boolean;
  enableHierarchicalSubAccountPositionsBlotter: boolean;
  filterResults: PositionsSubAccountsBlotterFoundation['filterResults'];
  groupingToggleButtons: ReturnType<typeof usePositionsSubAccountsBlotterGrouping>['groupingToggleButtons'];
  expandAllGroups: () => void;
  collapseAllGroups: () => void;
  columns: Column[];
}

export function usePositionsSubAccountsWsBlotter({
  blotterID,
  tab,
  foundation,
  columns: columnsOverride,
  gridOptions: additionalGridOptions,
  contextMenuOptions,
  getExtraMainMenuItems,
  wrapGroupingToggleButtons,
  useMobileLabels,
}: UsePositionsSubAccountsWsBlotterParams): UsePositionsSubAccountsWsBlotterResult {
  const {
    columns,
    persistedBlotterTable,
    filterResults,
    hierarchical,
    showZeroBalances,
    includeCash,
    enableHierarchicalSubAccountPositionsBlotter,
  } = foundation;

  const resolvedColumns = columnsOverride ?? columns;

  const refreshClientSideRowModel = useCallbackRef(() => {
    blotterTable.refreshClientSideRowModel();
  });

  const { blotterSpecToUse } = usePositionsSubAccountsBlotterVariants({
    hierarchical,
    refreshClientSideRowModel,
  });

  const { request: baseRequest, onStateUpdated: maybeModifyRequestOnStateUpdated } =
    useSubAccountPositionsBlotterRequest({
      initialGridState: persistedBlotterTable.initialState?.gridState,
      initialColumns: useConstant(columns),
      requestTag: blotterID,
      showZeroBalances,
      hierarchical,
    });

  const request = useMemo(
    () => ({
      ...baseRequest,
      ...removeEmptyFilters(filterResults.filter),
    }),
    [baseRequest, filterResults.filter]
  );

  const includeCashFilter = useIncludeCashFilterFunc({
    includeCash,
    assetGetter: getPositionAsset,
  });

  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionKey, filterFunc: includeCashFilter });

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
    maybeModifyRequestOnStateUpdated(event);
  });

  const blotterTable = useWsBlotterTable<WebsocketRequest, Position>({
    request,
    rowID: 'rowID' satisfies keyof Position,
    pipe: filterPipe,
    persistence: persistedBlotterTable,
    columns: resolvedColumns,
    getExtraMainMenuItems,
    contextMenuOptions,
    gridOptions: {
      ...blotterSpecToUse.gridOptionsOverlay,
      ...additionalGridOptions,
      onStateUpdated: handleGridStateUpdated,
    },
    exportOptions: {
      name: 'SubAccounts',
      tabLabel: tab.label,
    },
  });

  const { onGroupingStateUpdated, groupingToggleButtons } = usePositionsSubAccountsBlotterGrouping({
    setRowGroupColumns: blotterTable.setRowGroupColumns,
    wrap: wrapGroupingToggleButtons,
    useMobileLabels,
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  return {
    blotterTable: blotterTable as UseWsBlotterTable<Position>,
    hierarchical,
    showZeroBalances,
    includeCash,
    enableHierarchicalSubAccountPositionsBlotter,
    filterResults,
    groupingToggleButtons,
    expandAllGroups,
    collapseAllGroups,
    columns,
  };
}
